import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { v4 as uuidv4 } from 'uuid';
import {
  Brush,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import Form from 'react-bootstrap/Form';
import ChartContext from './DataEntrySection/DataEntryProvider';
import { wFlagMrCharttColumns, oocFlagMrCharttColumns } from '../components/Columns';
import {
  createCustomizedDot,
  getSigDigitsUtil,
  formatChartPointDateTime,
  withPopupDateTime
} from '../util/util';

function SQCMovingRangeChart({
  data,
  lines,
  syncId,
  xAxis,
  yMin,
  yMax,
  chartSigDigits,
  unit,
  showDateTimeInTooltip = false,
  tz,
  dateTimeFormat
}) {
  // Opt-in: date/time of the hovered point, formatted like the results table.
  const getPointDateTime = (row) =>
    showDateTimeInTooltip ? formatChartPointDateTime(row, tz, dateTimeFormat) : '';

  const formatXAxis = (tickItem) => {
    return String(data[tickItem]?.date) || '';
  };

  const [setupDataChecked, setSetupDataChecked] = useState(false);
  const [warningFlagsChecked, setWarningFlagsChecked] = useState(true);
  const [outOfControlChecked, setOutOfControlChecked] = useState(true);
  const [runChartsChecked, setRunChartsChecked] = useState(true);
  const { currentControlStrategyConfigurations } = useContext(ChartContext);

  const formatNothing = () => {
    return '';
  };

  const renderSigDigits = (stringValue) => {
    return getSigDigitsUtil(
      stringValue,
      currentControlStrategyConfigurations?.chartSigDigits || chartSigDigits
    );
  };

  const formatYAxis = (tickItem) => {
    return renderSigDigits(tickItem);
  };

  const handleSetupDataCheck = (e) => {
    setSetupDataChecked(e.target.checked);
  };

  const handleWarningFlagCheck = (e) => {
    setWarningFlagsChecked(e.target.checked);
  };

  const handleRunChartsCheck = (e) => {
    setRunChartsChecked(e.target.checked);
  };

  const handleOutOfControlCheck = (e) => {
    setOutOfControlChecked(e.target.checked);
  };

  const renderReferenceLines = (rcc) => {
    if (!rcc || data.length === 0) return null;

    return (
      <>
        {lines
          .filter((l) => l.reference)
          .map((line) => (
            <ReferenceLine
              key={`ref-${line.yAxis || line.name}`}
              y={line.overrideValue || data[data.length - 1]?.[line.yAxis]}
              stroke={line.color}
              strokeDasharray="3 3"
              label={line.label || ''}
              isAnimationActive={false}
            />
          ))}
      </>
    );
  };

  const renderCValue = (c) => {
    let valueToReturn = 0;
    if (c !== undefined) {
      valueToReturn = c - 3;
    }
    return valueToReturn;
  };

  const checkWarning = (payload) => {
    return wFlagMrCharttColumns.find((f) => !!payload[f]) !== undefined;
  };

  const checkOOC = (payload) => {
    return oocFlagMrCharttColumns.find((f) => !!payload[f]) !== undefined;
  };

  const CustomizedDot = ({ cx, cy, payload, dataKey }) => {
    if (dataKey !== 'mrOmits') return null;
    const isWarning = checkWarning(payload);
    const isOOC = checkOOC(payload);
    return createCustomizedDot(
      cx,
      cy,
      payload,
      setupDataChecked,
      isWarning,
      isOOC,
      warningFlagsChecked,
      outOfControlChecked
    );
  };

  const referenceLineValues = {};
  if (data.length > 0) {
    lines
      .filter((l) => l.reference)
      .forEach((refLine) => {
        referenceLineValues[refLine.yAxis] =
          refLine.overrideValue ?? data[data.length - 1]?.[refLine.yAxis] ?? null;
      });
  }

  const augmentedData = data.map((d) => ({
    ...d,
    ...referenceLineValues
  }));

  const uniqueParameters = Array.from(
    new Set(
      data
        .map((d) => {
          if (d.fullKey) {
            const parts = d.fullKey.split('|');
            return parts[parts.length - 1];
          }
          return d.parameter;
        })
        .filter(Boolean)
    )
  );

  return (
    <>
      <h5>
        Moving Range Chart
        {uniqueParameters?.length
          ? `: ${uniqueParameters.join(', ')}${unit?.trim() ? `, ${unit}` : ''}`
          : unit?.trim()
            ? `: ${unit}`
            : ''}
      </h5>

      <div className="checkbox-buttons-container">
        <div className="checkbox-buttons">
          Setup data = 1 <Form.Check value={setupDataChecked} onChange={handleSetupDataCheck} />
        </div>
        <div className="checkbox-buttons">
          Warning flags{' '}
          <Form.Check checked={warningFlagsChecked} onChange={handleWarningFlagCheck} />
        </div>
        <div className="checkbox-buttons">
          Out of Control{' '}
          <Form.Check checked={outOfControlChecked} onChange={handleOutOfControlCheck} />
        </div>
        <div className="checkbox-buttons">
          Reference Lines <Form.Check checked={runChartsChecked} onChange={handleRunChartsCheck} />
        </div>
      </div>
      <ResponsiveContainer height={400}>
        <LineChart
          data={augmentedData}
          syncId={syncId}
          margin={{
            top: 5,
            right: 20,
            bottom: 15,
            left: 20
          }}
        >
          {lines
            .filter((l) => !l.reference)
            .map((line) => (
              <Line
                type="linear"
                key={line.yAxis || line.name}
                isAnimationActive={false}
                dataKey={line.yAxis}
                stroke={line.color}
                name={line.name}
                dot={<CustomizedDot key={uuidv4()} />}
              />
            ))}
          {renderReferenceLines(runChartsChecked)}

          <CartesianGrid stroke="#ccc" strokeDasharray="5 5" />
          <XAxis dataKey={xAxis} tickFormatter={formatXAxis} />
          <YAxis type="number" tickFormatter={formatYAxis} domain={[yMin, yMax]} />
          <Brush dataKey={xAxis} ticketFormatter={formatNothing} />
          <Legend verticalAlign="top" height={36} />

          {lines
            .filter((l) => l.reference)
            .map((line) => (
              <Line
                key={`legend-${line.yAxis}`}
                type="linear"
                dataKey={line.yAxis}
                stroke={line.color}
                name={line.label || line.name}
                dot={false}
                legendType="line"
                isAnimationActive={false}
              />
            ))}

          {uniqueParameters.length > 1 ? (
            <Tooltip
              formatter={(value) => ` ${renderSigDigits(value)}`}
              labelFormatter={(label) => {
                let parameter = '';
                if (data && data[label] && data[label].fullKey) {
                  const parts = data[label].fullKey.split('|');
                  parameter = parts[parts.length - 1];
                } else if (data && data[label] && data[label].parameter) {
                  parameter = data[label].parameter;
                }
                const text = `${label}${parameter ? ` - ${parameter}` : ''}`;
                const pointDateTime = getPointDateTime(data && data[label]);
                return withPopupDateTime(text, pointDateTime);
              }}
            />
          ) : showDateTimeInTooltip ? (
            <Tooltip
              formatter={(value) => `! ${renderSigDigits(value)}`}
              labelFormatter={(label) => {
                const pointDateTime = getPointDateTime(data && data[label]);
                return withPopupDateTime(label, pointDateTime);
              }}
            />
          ) : (
            <Tooltip formatter={(value) => `! ${renderSigDigits(value)}`} />
          )}
        </LineChart>
      </ResponsiveContainer>
    </>
  );
}

SQCMovingRangeChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object).isRequired,
  lines: PropTypes.arrayOf(PropTypes.object).isRequired,
  xAxis: PropTypes.string.isRequired,
  syncId: PropTypes.string.isRequired,
  yMin: PropTypes.number.isRequired,
  yMax: PropTypes.number.isRequired,
  showDateTimeInTooltip: PropTypes.bool,
  tz: PropTypes.string,
  dateTimeFormat: PropTypes.string
};

export default SQCMovingRangeChart;
